import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Clickable from "@midland-industries/MobiusOverrides/Clickable/Clickable";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    catalogWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    catalogClickable?: ClickablePresentationProps;
    catalogButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    attributesGridItem?: GridItemProps;
    catalogGridItem?: GridItemProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    catalogClickable: {
        css: css`
            height: 100%;
            width: 100%;
            padding-bottom: 20px;
        `,
    },
    catalogButton: {
        color: "primary",
    },
    container: {
        css: css`
            border-bottom: 2px solid ${getColor("secondary.main")};
            margin-bottom: 10px;
        `,
    },
    attributesGridItem: { width: [12, 12, 8, 8, 8] },
    catalogGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            direction: rtl;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes, detail } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const catalogUrl = detail?.unspsc ? `http://catalog.midlandindustries.com/books/qhhy/#p=${detail.unspsc}` : null;

    const limitedAttributeTypes = attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? [];

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.attributesGridItem}>
                {" "}
                <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
                    {brand && (
                        <Typography data-test-selector="brand_item" as="p" {...styles.attributeTypeValuesText}>
                            <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                                {translate("Brand")}&nbsp;:&nbsp;
                            </Typography>
                            <span data-test-selector="brand_item_value">{brand.name}</span>
                        </Typography>
                    )}
                    {limitedAttributeTypes.map(attributeType => (
                        <Typography
                            key={attributeType.id.toString()}
                            data-test-selector="attributes_item"
                            data-attributetypeid={attributeType.id}
                            {...styles.attributeTypeValuesText}
                            as="p"
                        >
                            <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                            </Typography>
                            <span data-test-selector="attributes_item_value">
                                {(attributeType.attributeValues ?? [])
                                    .map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </span>
                        </Typography>
                    ))}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.catalogGridItem}>
                {catalogUrl && (
                    <StyledWrapper>
                        <Clickable href={catalogUrl} target="_blank" {...styles.catalogClickable}>
                            <Button {...styles.catalogButton}>{translate("View Catalog Page")}</Button>
                        </Clickable>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
